import { 
  collection, 
  addDoc, 
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  deleteDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { db, storage, auth } from './firebase';
import { BreadAnalysis } from '../types/analysis';

// Helper function to convert Firestore Timestamps to Dates
function convertTimestampsToDates(data: any): any {
  if (data instanceof Timestamp) {
    return data.toDate();
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertTimestampsToDates(item));
  }
  
  if (data && typeof data === 'object') {
    const converted: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(data)) {
      converted[key] = convertTimestampsToDates(value);
    }
    return converted;
  }
  
  return data;
}

// Helper function to convert dates to Firestore Timestamps
function convertDatesToTimestamps(data: any): any {
  if (data instanceof Date) {
    return Timestamp.fromDate(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertDatesToTimestamps(item));
  }
  
  if (data && typeof data === 'object') {
    const converted: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        converted[key] = convertDatesToTimestamps(value);
      }
    }
    return converted;
  }
  
  return data;
}

// Ensure user is authenticated
function getUserId(): string {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  return user.uid;
}

// Helper function to create a thumbnail from an image
async function createThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Calculate new dimensions (max 400px width/height)
      let width = img.width;
      let height = img.height;
      const maxDimension = 400;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create thumbnail'));
          }
        },
        'image/jpeg',
        0.7 // Quality setting
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
}

// Analysis Operations
export function subscribeToAnalyses(userId: string, callback: (analyses: BreadAnalysis[]) => void) {
  const analysesRef = collection(db, 'users', userId, 'analyses');
  const q = query(analysesRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const analyses = snapshot.docs.map(doc => ({
      ...convertTimestampsToDates(doc.data()),
      id: doc.id,
    } as BreadAnalysis));
    callback(analyses);
  });
}

export async function addAnalysis(
  analysis: Omit<BreadAnalysis, 'id' | 'createdAt'>,
  imageFile?: File
): Promise<void> {
  const userId = getUserId();
  const analysesRef = collection(db, 'users', userId, 'analyses');

  try {
    let imageUrl;
    let thumbnailUrl;

    if (imageFile) {
      // Generate unique IDs for both images
      const imageFileName = `${uuidv4()}.jpg`;
      const thumbnailFileName = `${uuidv4()}_thumb.jpg`;
      
      // Define paths
      const imagePath = `users/${userId}/analyses/${imageFileName}`;
      const thumbnailPath = `users/${userId}/analyses/${thumbnailFileName}`;
      
      // Create references
      const imageRef = ref(storage, imagePath);
      const thumbnailRef = ref(storage, thumbnailPath);

      // Create and upload thumbnail
      const thumbnailBlob = await createThumbnail(imageFile);
      const thumbnailUpload = uploadBytesResumable(thumbnailRef, thumbnailBlob);
      await thumbnailUpload;
      thumbnailUrl = await getDownloadURL(thumbnailRef);

      // Upload original image
      const imageUpload = uploadBytesResumable(imageRef, imageFile);
      await imageUpload;
      imageUrl = await getDownloadURL(imageRef);
    }

    // Create analysis document
    await addDoc(analysesRef, convertDatesToTimestamps({
      ...analysis,
      imageUrl,
      thumbnailUrl,
      createdAt: new Date()
    }));
  } catch (error) {
    console.error('Error adding analysis:', error);
    throw error;
  }
}

export async function deleteAnalysis(analysisId: string): Promise<void> {
  const userId = getUserId();
  const analysisRef = doc(db, 'users', userId, 'analyses', analysisId);

  try {
    // Get the analysis data first to check if there are images to delete
    const analysisSnap = await getDoc(analysisRef);
    if (!analysisSnap.exists()) {
      throw new Error('Analysis not found');
    }

    const analysisData = analysisSnap.data();
    
    // Delete both original image and thumbnail if they exist
    if (analysisData.imageUrl) {
      const imageRef = ref(storage, analysisData.imageUrl);
      try {
        await deleteObject(imageRef);
      } catch (error) {
        console.warn('Error deleting analysis image:', error);
      }
    }
    
    if (analysisData.thumbnailUrl) {
      const thumbnailRef = ref(storage, analysisData.thumbnailUrl);
      try {
        await deleteObject(thumbnailRef);
      } catch (error) {
        console.warn('Error deleting analysis thumbnail:', error);
      }
    }

    // Delete the analysis document
    await deleteDoc(analysisRef);
  } catch (error) {
    console.error('Error deleting analysis:', error);
    throw error;
  }
}